package ru.it_mozaika.sender.js.application.components.campaigns

import io.ktor.client.call.*
import js.core.jso
import mui.material.*
import mui.material.Size
import mui.system.responsive
import mui.system.sx
import oldcomponents.NotyType
import oldcomponents.reactEChartsCore
import react.*
import react.dom.aria.AriaRole
import react.dom.aria.ariaLabel
import react.dom.aria.ariaLabelledBy
import react.router.dom.useSearchParams
import ru.it_mozaika.components.*
import ru.it_mozaika.sender.common.models.EmailCampaignStatistics
import ru.it_mozaika.sender.common.models.EmailCampaignStatus
import ru.it_mozaika.sender.common.models.EmailCampaignTitle
import ru.it_mozaika.sender.js.application.Http
import ru.it_mozaika.utils.checkResponseError
import ru.it_mozaika.sender.js.application.context.useBackdrop
import ru.it_mozaika.sender.js.application.context.useStore
import ru.it_mozaika.sender.js.application.frames.MainCard
import tanstack.react.table.renderCell
import tanstack.react.table.useReactTable
import tanstack.react.virtual.UseVirtualizerOptions
import tanstack.react.virtual.useVirtualizer
import tanstack.table.core.ColumnDef
import tanstack.table.core.StringOrTemplateHeader
import tanstack.table.core.getCoreRowModel
import web.cssom.*
import web.html.HTMLDivElement

private val statisticsHeaders = listOf("Идентификатор", "Название", "Тема письма", "Отправлено", "Доставлено", "Прочитано", "Прочитано (уникальные)", "Клики", "Клики (уникальные)", "Ошибки")
private val eventNames = arrayOf("Отправлено", "Доставлено", "Прочитано", "Прочитано (уник)", "Клики", "Клики (уник)", "Ошибки")
val EmailCampaignListStatisticsFrame = VFC {
    val backdrop = useBackdrop()
    val showNotification = useSnackbar().enqueueSnackbar

    var selectDialogOpened by useState(false)
    var campaigns by useState(emptyMap<String, EmailCampaignTitle>())
    var campaignsIds by useState(emptyList<String>())

    var statistics by useState(mapOf<String, List<Pair<Int, Int>>>())
    var rawStatistics by useState(emptyMap<String, EmailCampaignStatistics>())

    var barNames by useState(arrayOf<String>())
    var barData by useState(kotlin.collections.List(7) { listOf<Int>() })

    useEffectOnce {
        Http.get(
            "/campaigns",
            mapOf(),
            builder = {
                url {
                    parameters.append("rows", "-1")
                    parameters.appendAll("status", listOf(EmailCampaignStatus.Complete.name))
                }
            }
        ) { response ->
            response.checkResponseError()?.let {
                oldcomponents.showNotification(it, NotyType.Error)
                return@get
            }
            val result = response.body<List<EmailCampaignTitle>>()
            campaigns = result.associateBy { it.id }
        }
    }

    useEffect(campaignsIds) {
        if (campaignsIds.isEmpty()) {
            rawStatistics = emptyMap()
            return@useEffect
        }

        Http.get("/campaigns/statistics", mapOf("ids" to campaignsIds.joinToString(","))) { response ->
            response.checkResponseError()?.let { error ->
                showNotification(error, jso { variant = AlertColor.error })
                return@get
            }
            val response = response.body<List<EmailCampaignStatistics>>()
            val result = response.associate { stats ->
                val totalSend = stats.variants.sumOf { it.send }
                val totalDelivery = stats.variants.sumOf { it.delivery }
                val totalOpen = stats.variants.sumOf { it.open }
                val totalOpenUnique = stats.variants.sumOf { it.openUnique }
                val totalClick = stats.variants.sumOf { it.click }
                val totalClickUnique = stats.variants.sumOf { it.clickUnique }
                val totalErrors = stats.variants.sumOf { it.errors }

                stats.id to listOf(
                    totalSend to 100,
                    totalDelivery to (totalDelivery * 100 / totalSend),
                    totalOpen to (totalOpen * 100 / totalDelivery),
                    totalOpenUnique to (totalOpenUnique * 100 / totalDelivery),
                    totalClick to (totalClick * 100 / totalOpen),
                    totalClickUnique to (totalClickUnique * 100 / totalOpen),
                    totalErrors to (totalErrors * 100 / totalSend),
                )
            }
            if (campaignsIds.size == result.size && campaignsIds.intersect(result.keys).size == result.size) {
                statistics = result
                rawStatistics = response.associateBy { it.id }
            }
        }
    }

    useEffect(statistics) {
        val result = MutableList(7) { listOf<Int>() }
        val names = statistics.map { (id, data) ->
            data.forEachIndexed { index, (value, _) -> result[index] = result[index].plus(value) }
            campaigns[id]?.name.orEmpty()
        }
        barData = result
        barNames = names.toTypedArray()
    }

    MainCard {
        title = "Статистика"
        actions = Stack.create {
            direction = responsive(StackDirection.row)
            Button {
                onClick = { selectDialogOpened = true }
                variant = ButtonVariant.contained
                +"Добавить в сравнение"
            }
        }

        TableContainer {

            Table {
                TableHead {
                    TableRow {
                        statisticsHeaders.forEach {
                            TableCell { +it }
                        }
                    }
                }
                TableBody {
                    campaignsIds.forEach { campaignId ->
                        TableRow {
                            key = "campaign-$campaignId"
                            sx {
                                "&:last-child td, &:last-child th" {
                                    border = Border(0.px, LineStyle.solid)
                                }
                            }
                            TableCell {
                                variant = TableCellVariant.head
                                scope = "row"
                                +campaignId
                            }
                            TableCell { +campaigns[campaignId]?.name.orEmpty() }
                            TableCell { +campaigns[campaignId]?.variants?.joinToString(",") { it.topic }.orEmpty() }
                            statistics[campaignId]?.let { stats ->
                                for (i in stats.indices) {
                                    TableCell {
                                        +"${stats[i].first} (${stats[i].second}%)"
                                    }
                                }
                            }
                        }
                    }
                }
            }

        }
    }

    MainCard {
        sx { marginTop = 24.px }
        reactEChartsCore {
            val labelOption = jso<dynamic> {
                show = true
                position = "insideBottom"
                distance = 15
                align = "left"
                verticalAlign = "middle"
                rotate = 90
                formatter = "{c}  {name|{a}}"
                fontSize = 16
                rich = jso {
                    name = jso {}
                }
            }
            option = jso {
                tooltip = jso {
                    trigger = "axis"
                    axisPointer = jso {
                        type = shadow
                    }
                }
                legend = jso {
                    this.data = eventNames
                }
                xAxis = jso {
                    type = "category"
                    axisTick = jso { show = false }
                    this.data = barNames.copyOf()
                }
                yAxis = jso { type = "value" }
                series = eventNames.mapIndexed { index, eventName ->
                    val value = barData[index].toTypedArray()
                    jso<dynamic> {
                        name = eventName
                        type = "bar"
                        barGap = 0
                        label = labelOption
                        emphasis = jso { focus = "series" }
                        this.data = value
                    }
                }.toTypedArray()
            }
        }
    }

    SelectCampaignsDialog {
        open = selectDialogOpened
        onClose = {
            selectDialogOpened = false
            campaignsIds = it
        }
        selectedCampaigns = campaignsIds
    }
}

external interface SelectCampaignsDialogProps : Props {
    var open: Boolean
    var onClose: (List<String>) -> Unit
    var selectedCampaigns: List<String>
}
private val tableColumns = listOf("id" to "ID", "name" to "Название")
val SelectCampaignsDialog = FC<SelectCampaignsDialogProps> { props ->
    val (params, setParams) = useSearchParams()
    val backdrop = useBackdrop()
    val showNotification = useSnackbar().enqueueSnackbar
    val store = useStore().currentStoreId
    var size by useState(0)
    var page by useState(params["page"]?.toIntOrNull() ?: 0)
    var rows by useState(params["rows"]?.toIntOrNull() ?: 10)
    var search by useState(params["search"].orEmpty())
    var campaigns by useState(emptyList<EmailCampaignTitle>())

    val (selectedCampaigns, setSelectedCampaigns) = useState(props.selectedCampaigns)
    useEffect(props.selectedCampaigns) {
        setSelectedCampaigns(props.selectedCampaigns)
    }

    fun loadCampaignsSize() {
        ru.it_mozaika.sender.js.application.frames.campaigns.loadCampaignsSize(
            store.orEmpty(),
            search,
            { showNotification(it, jso { variant = AlertColor.error }) }
        ) {
            backdrop.hide()
            size = it
        }
    }
    fun loadCampaigns() {
        ru.it_mozaika.sender.js.application.frames.campaigns.loadCampaigns(
            store.orEmpty(),
            page,
            rows,
            search,
            { showNotification(it, jso { variant = AlertColor.error }) }) {
            backdrop.hide()
            campaigns = it
        }
    }

    useEffect(props.open) {
        if (props.open) {
            backdrop.show()
            loadCampaignsSize()
            loadCampaigns()
        }
    }

    useEffect(store, search) { loadCampaignsSize() }
    useEffect(rows, page, search, store) { loadCampaigns() }

    val table = useReactTable<EmailCampaignTitle>(
        options = jso {
            this.columns = useMemo(tableColumns) {
                tableColumns.map { (column, title) ->
                    jso<ColumnDef<EmailCampaignTitle, Any?>> {
                        accessorFn = { item, _ ->
                            when (column) {
                                "id" -> item.id
                                "name" -> item.name
                                else -> ""
                            }

                        }
                        cell = tanstack.table.core.ColumnDefTemplate { context ->
                            ReactNode(context.getValue().toString())
                        }
                        id = column
                        header = StringOrTemplateHeader(title)
                    }
                }
            }.toTypedArray()
            this.data = useMemo(campaigns) { campaigns.toTypedArray() }
            this.state = jso {}
            this.onColumnVisibilityChange = {}
            this.onColumnOrderChange = {}
            this.getCoreRowModel = getCoreRowModel()
            this.debugTable = true
            this.debugHeaders = true
            this.debugColumns = true
        }
    )


    val parentRef = useRef<HTMLDivElement>()
    val virtualList = useVirtualizer(
        jso<UseVirtualizerOptions<HTMLDivElement, HTMLDivElement>>{
            this.getScrollElement = useCallback(parentRef.current){parentRef.current}
            this.estimateSize = {5}
            overscan = 30
            this.count = table.getRowModel().rows.size
        }
    )

    Dialog {
        open = props.open
        onClose = { _, _ -> props.onClose(props.selectedCampaigns) }
        DialogTitle { +"Выберите рассылки из списка" }
        DialogContent {
            Stack{
                direction = responsive(StackDirection.column)
                spacing = responsive(2)
                searchField{
                    this.onSearchQueryChange = {
                        search = it
                    }
                }
                TableContainer {
                    ref = parentRef
                    sx {
                        minHeight = 100.px
                        maxHeight = 500.px
                    }
                    Table {
                        ariaLabel = "campaigns-table"
                        ariaLabelledBy = "campaigns-table-title"
                        stickyHeader = true
                        TableBody {
                            virtualList.getVirtualItems().forEach { item ->
                                val row = table.getRowModel().rows[item.index]
                                val cellValue = row.getValue("id").toString()
                                val isItemSelected = selectedCampaigns.contains(cellValue)
                                val labelId = "campaigns-table-row-${item.index}"
                                TableRow {
                                    role = AriaRole.checkbox
                                    tabIndex = -1
                                    key = cellValue
                                    hover = true
                                    TableCell {
                                        padding = TableCellPadding.checkbox
                                        Checkbox {
                                            onClick = {
                                                setSelectedCampaigns {
                                                    if (it.contains(cellValue)) it.minus(cellValue) else it.plus(
                                                        cellValue
                                                    )
                                                }
                                            }
                                            color = CheckboxColor.primary
                                            checked = isItemSelected
                                            inputProps = jso {
                                                ariaLabelledBy = labelId
                                            }
                                        }
                                    }
                                    row.getVisibleCells().forEachIndexed { index, cell ->
                                        TableCell {
                                            if (index == 0) {
                                                id = labelId
                                                scope = "row"
                                                padding = TableCellPadding.none
                                                align = TableCellAlign.center
                                            }
                                            +renderCell(cell)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            TablePagination {
                this.page = page
                this.count = size
                this.rowsPerPage = rows
                onPageChange = { _, newPage ->
                    page = newPage.toInt()
                }
                onRowsPerPageChange = { event ->
                    rows = event.target.asDynamic().value as Int
                    page = 0
                }
            }
        }
        DialogActions {
            Button {
                this.onClick = { props.onClose(props.selectedCampaigns) }
                this.autoFocus = true
                this.size = Size.small
                +"Отмена"
            }
            Button {
                this.onClick = { props.onClose(selectedCampaigns) }
                this.variant = ButtonVariant.contained
                this.size = Size.small
                +"Сохранить"
            }
        }
    }
}
